class CartDrawerUpsell extends HTMLElement {
  connectedCallback() {
    this.addEventListener('submit', this.#handleSubmit);
    this.addEventListener('change', this.#handleChange);
    document.addEventListener('cart:update', this.#handleCartUpdate);
    window.addEventListener('load', this.#handleWindowLoad);
    this.#syncShippingBars();
    this.#initialSyncTimer = window.setTimeout(this.#handleInitialSync, 250);
  }

  disconnectedCallback() {
    this.removeEventListener('submit', this.#handleSubmit);
    this.removeEventListener('change', this.#handleChange);
    document.removeEventListener('cart:update', this.#handleCartUpdate);
    window.removeEventListener('load', this.#handleWindowLoad);
    window.clearTimeout(this.#initialSyncTimer);
    this.#cartUpdateRequest += 1;
  }

  #cartUpdateRequest = 0;
  #initialSyncTimer;

  #handleInitialSync = () => {
    if (this.isConnected) this.#syncShippingBars();
  };

  #handleWindowLoad = () => {
    this.#syncShippingBars();
  };

  #handleCartUpdate = async (event) => {
    if (event.detail?.data?.didError) return;

    const requestId = ++this.#cartUpdateRequest;
    const eventCart = event.detail?.resource;
    const eventTotalPrice = Number(eventCart?.total_price);

    if (Number.isFinite(eventTotalPrice)) {
      this.#updateShippingBarsAfterCartChange(eventTotalPrice, eventCart?.currency, requestId);
      return;
    }

    const cart = await this.#fetchCart();
    if (requestId !== this.#cartUpdateRequest || !this.isConnected || !cart) return;

    this.#updateShippingBarsAfterCartChange(Number(cart.total_price), cart.currency, requestId);
  };

  #updateShippingBarsAfterCartChange(totalPrice, currency, requestId) {
    const update = () => {
      if (requestId !== this.#cartUpdateRequest || !this.isConnected) return;
      this.#updateShippingBars(totalPrice, currency);
    };

    update();
    queueMicrotask(update);
    window.requestAnimationFrame?.(update);
  }

  #handleChange = (event) => {
    const input = event.target;
    if (input instanceof HTMLSelectElement && input.matches('[data-cart-drawer-oto-variant-select]')) {
      this.#updateUpsellVariant(input);
      return;
    }

    if (input instanceof HTMLSelectElement && input.matches('[data-cart-drawer-upsell-variant-select]')) {
      this.#updateUpsellVariant(input);
      return;
    }

    if (!(input instanceof HTMLInputElement) || !input.matches('[data-cart-drawer-oto-checkbox]')) return;
    if (!input.checked) return;

    const form = input.closest('form');
    if (!(form instanceof HTMLFormElement)) return;

    form.requestSubmit();
  };

  #handleSubmit = async (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement) || !this.contains(form)) return;

    const trigger = form.querySelector('[data-cart-drawer-upsell-add]');
    const variantId = trigger?.getAttribute('data-variant-id') || form.querySelector('[name="id"]')?.value;
    if (!variantId || !(trigger instanceof HTMLElement)) return;

    event.preventDefault();

    const label = trigger.querySelector('[data-cart-drawer-upsell-label]');
    const addLabel = this.dataset.addLabel || 'Add';
    const addingLabel = this.dataset.addingLabel || 'Adding...';
    const addedLabel = this.dataset.addedLabel || 'Added';
    const errorLabel = this.dataset.addError || 'Error while adding to cart.';
    const originalLabel = label?.textContent || trigger.textContent || addLabel;
    const originalAriaLabel = trigger.getAttribute('aria-label') || originalLabel;
    const error = form.querySelector('[data-cart-drawer-upsell-error]');

    if ('disabled' in trigger) trigger.disabled = true;
    trigger.setAttribute('aria-label', addingLabel);
    if (error) {
      error.textContent = '';
      error.classList.add('hidden');
    }

    try {
      const sections = this.#getCartSectionIds();
      const response = await fetch(this.#cartAddUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({
          items: [
            {
              id: Number(variantId),
              quantity: 1,
              properties: this.#getLineItemProperties(form),
            },
          ],
          sections: sections.join(','),
          sections_url: window.location.pathname,
        }),
      });

      const data = await response.json();

      if (!response.ok || data.status) {
        throw new Error(data.description || data.message || errorLabel);
      }

      const updateData = await this.#applyCartMetadata(form, sections);
      const cart = await this.#fetchCart();
      const itemCount = cart?.item_count ?? 1;
      const eventSource = cart ? 'cart-drawer-upsell' : 'product-form-component';
      const sectionsHtml = updateData?.sections || data.sections || {};

      if (cart) {
        this.#updateShippingBars(cart.total_price, cart.currency);
      }

      document.dispatchEvent(
        new CustomEvent('cart:update', {
          bubbles: true,
          detail: {
            resource: cart || { item_count: itemCount },
            sourceId: 'cart-drawer-upsell',
            data: {
              source: eventSource,
              itemCount,
              variantId,
              sections: sectionsHtml,
            },
          },
        })
      );

      trigger.setAttribute('aria-label', addedLabel);

      window.setTimeout(() => {
        if (label) label.textContent = originalLabel;
        trigger.setAttribute('aria-label', originalAriaLabel);
        if ('disabled' in trigger) trigger.disabled = false;
      }, 1200);
    } catch (error) {
      console.error(error);
      this.#fallbackSubmit(form);
    }
  };

  #updateUpsellVariant(select) {
    const form = select.closest('form');
    if (!(form instanceof HTMLFormElement)) return;

    const variantId = select.value;
    const selectedOption = select.selectedOptions[0];
    const variantInput = form.querySelector('[data-cart-drawer-upsell-variant-input]');
    const trigger = form.querySelector('[data-cart-drawer-upsell-add]');
    const price = form.querySelector('[data-cart-drawer-upsell-price], [data-cart-drawer-oto-price]');

    if (variantInput instanceof HTMLInputElement) {
      variantInput.value = variantId;
    }

    if (trigger instanceof HTMLElement) {
      trigger.setAttribute('data-variant-id', variantId);
    }

    if (price instanceof HTMLElement && selectedOption?.dataset.price) {
      price.textContent = selectedOption.dataset.price;
    }
  }

  #getLineItemProperties(form) {
    const properties = {};

    for (const input of form.querySelectorAll('[name^="properties["]')) {
      if (
        !(
          input instanceof HTMLInputElement ||
          input instanceof HTMLTextAreaElement ||
          input instanceof HTMLSelectElement
        ) ||
        !input.name
      )
        continue;

      const propertyName = input.name.replace(/^properties\[/, '').replace(/\]$/, '');
      if (propertyName && input.value) properties[propertyName] = input.value;
    }

    return properties;
  }

  async #applyCartMetadata(form, sections) {
    const isOtoForm = Boolean(form.dataset.cartDrawerOtoTag || form.dataset.cartDrawerOtoDiscountCode);

    if (!isOtoForm) return null;

    const otoTag = form.dataset.cartDrawerOtoTag || 'OTO';
    const discountCode = form.dataset.cartDrawerOtoDiscountCode?.trim();
    const body = {
      attributes: {
        OTO: 'true',
        OTO_Tag: otoTag,
      },
      sections,
      sections_url: window.location.pathname,
    };

    if (discountCode) {
      body.discount = this.#mergeDiscountCodes(discountCode);
    }

    const response = await fetch(this.#cartUpdateUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify(body),
    });

    if (!response.ok) return null;

    return await response.json();
  }

  #mergeDiscountCodes(discountCode) {
    const existingCodes = [];

    for (const pill of document.querySelectorAll('.cart-discount__pill')) {
      if (pill instanceof HTMLElement && pill.dataset.discountCode) {
        existingCodes.push(pill.dataset.discountCode);
      }
    }

    if (!existingCodes.includes(discountCode)) {
      existingCodes.push(discountCode);
    }

    return existingCodes.join(',');
  }

  get #cartAddUrl() {
    return window.Theme?.routes?.cart_add_url || '/cart/add.js';
  }

  get #cartUpdateUrl() {
    return window.Theme?.routes?.cart_update_url || '/cart/update.js';
  }

  #getCartSectionIds() {
    const sectionIds = [];

    for (const component of document.querySelectorAll('cart-items-component')) {
      if (component instanceof HTMLElement && component.dataset.sectionId) {
        sectionIds.push(component.dataset.sectionId);
      }
    }

    return [...new Set(sectionIds)];
  }

  async #fetchCart() {
    try {
      const response = await fetch('/cart.js', {
        headers: { Accept: 'application/json' },
      });

      if (!response.ok) return null;

      return await response.json();
    } catch (_) {
      return null;
    }
  }

  #syncShippingBars() {
    const bar = this.querySelector('[data-cart-drawer-shipping]');
    if (!(bar instanceof HTMLElement)) return;

    const totalPrice = Number(bar.dataset.cartTotal);
    if (!Number.isFinite(totalPrice)) return;

    this.#updateShippingBars(totalPrice, bar.dataset.currency);
  }

  #updateShippingBars(totalPrice, currency) {
    if (!Number.isFinite(totalPrice)) return;

    for (const bar of document.querySelectorAll('[data-cart-drawer-shipping]')) {
      if (!(bar instanceof HTMLElement)) continue;

      const baseFreeShippingThreshold = Number(bar.dataset.freeShippingThreshold || 0);
      const baseGiftThreshold = Number(bar.dataset.giftThreshold || 0);
      const baseDiscountThreshold = Number(bar.dataset.discountThreshold || baseGiftThreshold || 0);
      const thresholdCurrency = bar.dataset.thresholdCurrency || 'EUR';
      const freeShippingThresholdCurrency = bar.dataset.freeShippingThresholdCurrency || thresholdCurrency;
      const activeCurrency = currency || bar.dataset.currency || thresholdCurrency;
      const currencyRate = thresholdCurrency === activeCurrency ? 1 : null;
      let freeShippingCurrencyRate = null;

      if (freeShippingThresholdCurrency === activeCurrency) {
        freeShippingCurrencyRate = 1;
      } else if (freeShippingThresholdCurrency === thresholdCurrency) {
        freeShippingCurrencyRate = currencyRate;
      }

      bar.dataset.cartTotal = String(totalPrice);
      bar.dataset.currency = activeCurrency;

      if (currencyRate === null || freeShippingCurrencyRate === null) {
        this.#resetShippingBar(
          bar,
          baseFreeShippingThreshold,
          baseGiftThreshold,
          baseDiscountThreshold,
          freeShippingThresholdCurrency,
          thresholdCurrency
        );
        continue;
      }

      const freeShippingThreshold = this.#roundThresholdUp(baseFreeShippingThreshold, freeShippingCurrencyRate);
      const giftThreshold = this.#roundThresholdUp(baseGiftThreshold, currencyRate);
      const discountThreshold = this.#roundThresholdUp(baseDiscountThreshold, currencyRate);
      if (discountThreshold <= 0) continue;

      const progress = Math.min((totalPrice / discountThreshold) * 100, 100);
      const shippingMilestonePosition = Math.min((freeShippingThreshold / discountThreshold) * 100, 100);
      const giftMilestonePosition = Math.min((giftThreshold / discountThreshold) * 100, 100);
      const freeShippingRemaining = freeShippingThreshold - totalPrice;
      const giftRemaining = giftThreshold - totalPrice;
      const discountRemaining = discountThreshold - totalPrice;

      bar.style.setProperty('--cart-drawer-shipping-progress', `${progress}%`);
      bar.style.setProperty('--cart-drawer-shipping-milestone', `${shippingMilestonePosition}%`);
      bar.style.setProperty('--cart-drawer-gift-milestone', `${giftMilestonePosition}%`);
      bar.classList.toggle('cart-drawer-upsell__shipping--unlocked', freeShippingRemaining <= 0);
      bar.classList.toggle('cart-drawer-upsell__shipping--gift-unlocked', giftRemaining <= 0);
      bar.classList.toggle('cart-drawer-upsell__shipping--discount-unlocked', discountRemaining <= 0);

      const message = bar.querySelector('[data-cart-drawer-shipping-message]');
      if (message) {
        if (freeShippingRemaining > 0) {
          message.textContent = (bar.dataset.freeShippingMessage || '').replace(
            '[amount]',
            this.#formatMoney(freeShippingRemaining, activeCurrency)
          );
        } else if (giftRemaining > 0) {
          message.textContent = bar.dataset.freeShippingSuccessMessage || '';
        } else if (discountRemaining > 0) {
          message.textContent = bar.dataset.giftSuccessMessage || '';
        } else {
          message.textContent = bar.dataset.discountSuccessMessage || '';
        }
      }

      const next = bar.querySelector('[data-cart-drawer-shipping-next]');
      if (next instanceof HTMLElement) {
        next.textContent = (bar.dataset.nextDiscountMessage || '').replace(
          '[amount]',
          this.#formatThresholdMoney(discountThreshold, activeCurrency)
        );
        next.hidden = discountRemaining <= 0;
      }

      const giftThresholdAmount = bar.querySelector('[data-cart-drawer-gift-threshold-amount]');
      if (giftThresholdAmount instanceof HTMLElement) {
        giftThresholdAmount.textContent = this.#formatThresholdMoney(giftThreshold, activeCurrency);
      }
    }
  }

  #roundThresholdUp(baseThreshold, currencyRate) {
    return Math.ceil((baseThreshold * currencyRate) / 100) * 100;
  }

  #resetShippingBar(
    bar,
    freeShippingThreshold,
    giftThreshold,
    discountThreshold,
    freeShippingCurrency,
    thresholdCurrency
  ) {
    bar.style.setProperty('--cart-drawer-shipping-progress', '0%');
    bar.classList.remove(
      'cart-drawer-upsell__shipping--unlocked',
      'cart-drawer-upsell__shipping--gift-unlocked',
      'cart-drawer-upsell__shipping--discount-unlocked'
    );

    const message = bar.querySelector('[data-cart-drawer-shipping-message]');
    if (message) {
      message.textContent = (bar.dataset.freeShippingMessage || '').replace(
        '[amount]',
        this.#formatMoney(freeShippingThreshold, freeShippingCurrency)
      );
    }

    const next = bar.querySelector('[data-cart-drawer-shipping-next]');
    if (next instanceof HTMLElement) {
      next.textContent = (bar.dataset.nextDiscountMessage || '').replace(
        '[amount]',
        this.#formatThresholdMoney(discountThreshold, thresholdCurrency)
      );
      next.hidden = false;
    }

    const giftThresholdAmount = bar.querySelector('[data-cart-drawer-gift-threshold-amount]');
    if (giftThresholdAmount instanceof HTMLElement) {
      giftThresholdAmount.textContent = this.#formatThresholdMoney(giftThreshold, thresholdCurrency);
    }
  }

  #formatMoney(value, currency = 'EUR') {
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'it-IT', {
        style: 'currency',
        currency,
      }).format(value / 100);
    } catch (_) {
      return `€${(value / 100).toFixed(2)}`;
    }
  }

  #formatThresholdMoney(value, currency = 'EUR') {
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'it-IT', {
        style: 'currency',
        currency,
        maximumFractionDigits: 0,
      }).format(value / 100);
    } catch (_) {
      return `${Math.ceil(value / 100)} ${currency}`;
    }
  }

  #fallbackSubmit(form) {
    const fallback = form.cloneNode(true);
    if (!(fallback instanceof HTMLFormElement)) return;

    fallback.style.display = 'none';
    fallback.action = form.action || '/cart/add';
    fallback.method = 'post';

    document.body.appendChild(fallback);
    fallback.submit();
  }
}

if (!customElements.get('cart-drawer-upsell')) {
  customElements.define('cart-drawer-upsell', CartDrawerUpsell);
}
